/**
 * Turns a failed Keycloak sign-in into copy that says what happened and what
 * to do next.
 *
 * KeycloakAdminService.authenticate rethrows the admin client's NetworkError
 * with `statusCode` added. The raw Keycloak body sits on `responseData`
 * ({ error, error_description }); a request that never reached the server is
 * a TypeError with no status.
 */

export type SignInErrorKind =
  | 'credentials'
  | 'locked'
  | 'disabled'
  | 'setup'
  | 'rate'
  | 'network'
  | 'server'
  | 'client'
  | 'unknown';

export interface SignInError {
  kind: SignInErrorKind;
  title: string;
  message: string;
  /** Offer the reset-password link alongside the message. */
  offerReset: boolean;
  /** The field the error is about, to focus and mark invalid. */
  field: 'email' | 'password' | null;
}

function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

export function describeSignInError(error: any, online = true): SignInError {
  const status: number | undefined =
    error?.statusCode ?? error?.response?.status ?? error?.status;
  const data = error?.responseData ?? error?.response?.data ?? error?.data;
  const code = text(data?.error).toLowerCase();
  const description = (
    text(data?.error_description) ||
    text(data?.message) ||
    (typeof data === 'string' ? data : '')
  ).toLowerCase();

  if (!online || (!status && (error instanceof TypeError || /fetch|network|load failed/i.test(text(error?.message))))) {
    return {
      kind: 'network',
      title: 'Can’t reach the sign-in server',
      message: 'Check your internet connection, then try again.',
      offerReset: false,
      field: null,
    };
  }

  if (/temporarily disabled|locked/.test(description)) {
    return {
      kind: 'locked',
      title: 'This account is locked for now',
      message:
        'Too many wrong passwords locked it for a few minutes. Wait, then try again, or reset your password.',
      offerReset: true,
      field: null,
    };
  }

  if (/disabled/.test(description)) {
    return {
      kind: 'disabled',
      title: 'This account is turned off',
      message: 'Ask an administrator in your organization to turn it back on.',
      offerReset: false,
      field: null,
    };
  }

  if (/not fully set up|required action/.test(description)) {
    return {
      kind: 'setup',
      title: 'Your account setup isn’t finished',
      message: 'Reset your password to finish setting up your account, then sign in.',
      offerReset: true,
      field: null,
    };
  }

  if (status === 429) {
    return {
      kind: 'rate',
      title: 'Too many sign-in attempts',
      message: 'Wait a minute, then try again.',
      offerReset: false,
      field: null,
    };
  }

  if (code === 'unauthorized_client' || code === 'invalid_client') {
    return {
      kind: 'client',
      title: 'Sign-in isn’t set up for this portal',
      message: 'This is a configuration problem on our side. Contact Black Konnect support.',
      offerReset: false,
      field: null,
    };
  }

  if (code === 'invalid_grant' || status === 401 || /invalid user credentials/.test(description)) {
    return {
      kind: 'credentials',
      title: 'Wrong email, username or password',
      message: 'Check both and try again. Passwords are case-sensitive.',
      offerReset: true,
      field: 'password',
    };
  }

  if (typeof status === 'number' && status >= 500) {
    return {
      kind: 'server',
      title: 'Sign-in isn’t responding',
      message: 'The sign-in server had a problem. Try again in a few minutes.',
      offerReset: false,
      field: null,
    };
  }

  return {
    kind: 'unknown',
    title: 'Couldn’t sign you in',
    message:
      text(error?.userMessage) && !/network response was not ok/i.test(text(error?.userMessage))
        ? text(error.userMessage)
        : 'Something went wrong. Try again.',
    offerReset: false,
    field: null,
  };
}
